import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../src/store/store" ;
import {
  createFeedback,
  resetFeedback,
  clearError as clearFeedbackError
} from "../../src/store/slices/FeedBack/Index";
import Swal from "sweetalert2";

interface TechnicalSupportProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface Translations {
  [key: string]: {
    technicalSupport: string;
    name: string;
    email: string;
    message: string;
    send: string;
    cancel: string;
    namePlaceholder: string;
    phonePlaceholder: string;
    emailPlaceholder: string;
    messagePlaceholder: string;
    loading: string;
    supportSuccess: string;
    ok: string;
    supportDescription: string;
    mobileNumber: string;
  };
}

const translations: Translations = {
  az: {
    technicalSupport: "Texniki dəstək",
    name: "Ad və soyad",
    email: "E-poçt",
    message: "Mesaj",
    send: "Göndər",
    cancel: "Ləğv et",
    namePlaceholder: "Adınızı daxil edin",
    phonePlaceholder: "+994 XX XXX XX XX",
    emailPlaceholder: "example@mail.com",
    messagePlaceholder: "Mesajınızı yazın...",
    loading: "Yüklənir...",
    supportSuccess: "Mesajınız uğurla göndərildi",
    ok: "Tamam",
    supportDescription: "Şəxsi kabinetə giriş zamanı hər hansı texniki xəta və ya çətinliklə qarşılaşdığınız halda, bu forma vasitəsilə bizə müraciət edə bilərsiniz. Müraciətiniz ən qısa müddətdə araşdırılacaq və sizə müvafiq texniki dəstək göstəriləcəkdir.",
    mobileNumber: "Mobil nömrə"
  },
  ru: {
    technicalSupport: "Техническая поддержка",
    name: "Имя и фамилия",
    email: "Электронная почта",
    message: "Сообщение",
    send: "Отправить",
    cancel: "Отмена",
    namePlaceholder: "Введите ваше имя",
    phonePlaceholder: "+994 XX XXX XX XX",
    emailPlaceholder: "example@mail.com",
    messagePlaceholder: "Напишите ваше сообщение...",
    loading: "Загрузка...",
    supportSuccess: "Ваше сообщение успешно отправлено",
    ok: "Хорошо",
    supportDescription: "Если вы столкнулись с какими-либо техническими ошибками или трудностями при входе в личный кабинет, вы можете связаться с нами через эту форму. Ваше обращение будет рассмотрено в кратчайшие сроки, и вам будет предоставлена соответствующая техническая поддержка.",
    mobileNumber: "Мобильный номер"
  },
  en: {
    technicalSupport: "Technical Support",
    name: "Full name",
    email: "Email",
    message: "Message",
    send: "Send",
    cancel: "Cancel",
    namePlaceholder: "Enter your name",
    phonePlaceholder: "+994 XX XXX XX XX",
    emailPlaceholder: "example@mail.com",
    messagePlaceholder: "Write your message...",
    loading: "Loading...",
    supportSuccess: "Your message has been sent successfully",
    ok: "OK",
    supportDescription: "If you encounter any technical errors or difficulties when logging into your personal account, you can contact us through this form. Your request will be reviewed as soon as possible and you will be provided with appropriate technical support.",
    mobileNumber: "Mobile number"
  }
};

export default function TechnicalSupport({ isOpen, onClose, currentLanguage }: TechnicalSupportProps) {
  const dispatch = useDispatch<AppDispatch>();
  const t = translations[currentLanguage] || translations.az;

  const [supportForm, setSupportForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const { loading: feedbackLoading, success: feedbackSuccess, error: feedbackError, validationErrors } = useSelector(
    (state: RootState) => state.feedback
  );

  // Handle modal visibility effect
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle feedback success
  useEffect(() => {
    if (feedbackSuccess) {
      // First close the modal
      handleClose();

      // Then show success alert after a short delay
      setTimeout(() => {
        Swal.fire({
          icon: 'success',
          title: t.supportSuccess,
          confirmButtonText: t.ok,
          confirmButtonColor: '#a80067',
        });
      }, 300); // 300ms delay to ensure modal is fully closed
    }
  }, [feedbackSuccess, t]);

  const handleClose = () => {
    dispatch(clearFeedbackError());
    setSupportForm({
      name: '',
      phone: '',
      email: '',
      message: ''
    });
    dispatch(resetFeedback());
    onClose();
  };

  const handleFormChange = (field: string, value: string) => {
    setSupportForm(prev => ({
      ...prev,
      [field]: value
    }));
    dispatch(clearFeedbackError());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearFeedbackError());

    await dispatch(
      createFeedback({
        requestData: {
          name: supportForm.name,
          email: supportForm.email,
          phone: supportForm.phone,
          feedback: supportForm.message
        },
        language: currentLanguage as 'az' | 'en'
      })
    );
  };

  const getFieldError = (fieldName: string): string => {
    if (validationErrors) {
      const error = validationErrors.find(err => err.attr === fieldName);
      return error ? error.detail : '';
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 animate-fadeIn"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-t-2xl w-full sm:w-1/2 max-h-[90vh] overflow-hidden relative animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-[80vh]">
          {/* Fixed header */}
          <div className="px-8 pt-4 pb-4 border-b border-gray-200 bg-white flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-800 pr-8">
              {t.technicalSupport}
            </h2>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-8 py-6 flex-1">
            {/* General Feedback Error */}
            {feedbackError && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-600 text-sm">{feedbackError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Description */}
              <div>
                <p className="mb-4 text-[#785b6c]">{t.supportDescription}</p>

                {/* Name field */}
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.name}
                </label>
                <input
                  type="text"
                  value={supportForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder={t.namePlaceholder}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                {getFieldError('name') && (
                  <p className="text-red-500 text-sm mt-1">{getFieldError('name')}</p>
                )}
              </div>

              {/* Phone field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.mobileNumber}
                </label>
                <input
                  type="tel"
                  value={supportForm.phone}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === "") {
                      handleFormChange('phone', "+994 ");
                      return;
                    }

                    if (!value.startsWith("+994 ")) {
                      handleFormChange('phone', "+994 ");
                      return;
                    }

                    const digitsOnly = value.slice(5).replace(/\D/g, '');

                    if (digitsOnly.length <= 9) {
                      handleFormChange('phone', `+994 ${digitsOnly}`);
                    }
                  }}
                  onFocus={(e) => {
                    if (!supportForm.phone || supportForm.phone === "") {
                      handleFormChange('phone', "+994 ");
                      setTimeout(() => {
                        e.target.setSelectionRange(5, 5);
                      }, 0);
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const digits = pastedText.replace(/\D/g, '');

                    if (digits.length >= 9) {
                      const last9Digits = digits.slice(-9);
                      handleFormChange('phone', `+994 ${last9Digits}`);
                    } else if (digits.length > 0) {
                      const currentDigits = supportForm.phone.slice(5).replace(/\D/g, '');
                      const newDigits = (currentDigits + digits).slice(0, 9);
                      handleFormChange('phone', `+994 ${newDigits}`);
                    }
                  }}
                  placeholder={t.phonePlaceholder}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                {getFieldError('phone') && (
                  <p className="text-red-500 text-sm mt-1">{getFieldError('phone')}</p>
                )}
              </div>

              {/* Email field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.email}
                </label>
                <input
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder={t.emailPlaceholder}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                {getFieldError('email') && (
                  <p className="text-red-500 text-sm mt-1">{getFieldError('email')}</p>
                )}
              </div>

              {/* Message field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.message}
                </label>
                <textarea
                  value={supportForm.message}
                  onChange={(e) => handleFormChange('message', e.target.value)}
                  placeholder={t.messagePlaceholder}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                />
                {getFieldError('feedback') && (
                  <p className="text-red-500 text-sm mt-1">{getFieldError('feedback')}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2 pb-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={feedbackLoading}
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={feedbackLoading}
                  className={`flex-1 px-4 py-3 text-white rounded-lg transition-all font-medium ${
                    feedbackLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ backgroundColor: "#a80067" }}
                >
                  {feedbackLoading ? t.loading : t.send}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}